import { z } from 'zod';
import { PropertyCategorySchema, PropertySubtypeSchema } from '../common/property-taxonomy.schema';
import { refersTo } from '../common/refers-to';
import { DataFieldKeySchema } from '../identity/document-template-data-fields';
import { LegalFormSchema } from '../identity/company.schemas';
import { CreateOrganizationBodySchema } from '../identity/organizations.schemas';
import { DOCUMENT_TEMPLATE_CATEGORIES, DocumentTemplateContentSchema } from '../identity/document-templates.schemas';
import { CommissionStatementSchema } from '../property/buyer-commission';
import { MediaFileReadSchema } from '../media/files.schemas';
import { GetDealPathParamsSchema } from './deals.schemas';
import { MandateTypeSchema } from './mandate-terms';
import { SigningSummarySchema } from './deal-signing-requests.schemas';

export const GENERATED_DOCUMENT_TYPES = DOCUMENT_TEMPLATE_CATEGORIES;
export const GeneratedDocumentTypeSchema = z.enum(GENERATED_DOCUMENT_TYPES);
export type GeneratedDocumentType = z.infer<typeof GeneratedDocumentTypeSchema>;

export const DOCUMENT_LOCATION_OPTIONS = ['LOCATION', 'HEADQUARTERS'] as const;
export const DocumentLocationOptionSchema = z.enum(DOCUMENT_LOCATION_OPTIONS);
export type DocumentLocationOption = z.infer<typeof DocumentLocationOptionSchema>;

export const DealDocumentDraftQuerySchema = z.object({
  templateId: z.uuid(),
  locationOption: DocumentLocationOptionSchema.optional(),
});
export type DealDocumentDraftQuery = z.infer<typeof DealDocumentDraftQuerySchema>;

// `position` is set for the `Eigentümer 1`–`Eigentümer 4` groups only.
export const DataFieldRefSchema = z.object({
  key: DataFieldKeySchema,
  position: z.number().int().min(1).max(4).nullable(),
});
export type DataFieldRef = z.infer<typeof DataFieldRefSchema>;

export const DocumentAddressSchema = z.object({
  street: z.string().nullable(),
  houseNumber: z.string().nullable(), // null for a Kontakt address: its `street` carries the number
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
});
export type DocumentAddress = z.infer<typeof DocumentAddressSchema>;

export const DocumentValueSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('TEXT'), text: z.string() }),
  z.object({ kind: z.literal('DATE'), date: z.iso.date() }),
  z.object({ kind: z.literal('MONEY'), amount: z.number() }), // EUR
  z.object({ kind: z.literal('PERCENT'), value: z.number() }),
  z.object({ kind: z.literal('COMMISSION_STATEMENT'), statement: CommissionStatementSchema }),
  z.object({ kind: z.literal('MONTHS'), months: z.number().int() }),
  z.object({ kind: z.literal('ADDRESS'), address: DocumentAddressSchema }),
  z.object({ kind: z.literal('NAME_LIST'), names: z.array(z.string()).min(1) }),
  z.object({ kind: z.literal('LINES'), lines: z.array(z.string()).min(1) }),
  z.object({ kind: z.literal('BROKER_REPRESENTATION'), names: z.array(z.string()).min(1) }),
  z.object({
    kind: z.literal('ADDRESS_BLOCKS'),
    blocks: z.array(z.object({ name: z.string(), address: DocumentAddressSchema.nullable() })).min(1),
  }),
  z.object({ kind: z.literal('MANDATE_TYPE'), token: MandateTypeSchema }),
  z.object({ kind: z.literal('LEGAL_FORM'), token: LegalFormSchema }),
  z.object({
    kind: z.literal('PROPERTY_TYPE'),
    category: PropertyCategorySchema,
    subtype: PropertySubtypeSchema.nullable(),
  }),
]);
export type DocumentValue = z.infer<typeof DocumentValueSchema>;

// An input's own value: a DocumentValue, plus the split person name an editor needs.
export const DocumentInputValueSchema = z.union([
  DocumentValueSchema,
  z.object({ kind: z.literal('PERSON_NAME'), firstName: z.string(), lastName: z.string() }),
]);
export type DocumentInputValue = z.infer<typeof DocumentInputValueSchema>;

export const DOCUMENT_SOURCE_INPUTS = {
  OWNER: ['NAME', 'ADDRESS', 'DATE_OF_BIRTH', 'NATIONALITY', 'BIRTH_NAME', 'BIRTH_PLACE', 'EMAIL', 'PHONE'],
  PROPERTY: ['ADDRESS'],
  DEAL: [
    'EXPECTED_SALE_PRICE',
    'MANDATE_TYPE',
    'MANDATE_START',
    'MANDATE_DURATION',
    'SELLER_COMMISSION',
    'BUYER_COMMISSION',
  ],
  // MANAGERS is never a value: it stands for "this Standort has a Standortleitung".
  LOCATION: ['NAME', 'ADDRESS', 'MANAGERS'],
  BROKER: ['NAME', 'EMAIL', 'PHONE'],
  COMPANY: [
    'COMPANY_NAME',
    'LEGAL_FORM',
    'ADDRESS',
    'MANAGING_DIRECTORS',
    'REGISTER_COURT',
    'REGISTER_NUMBER',
    'VAT_ID',
    'TRADE_PERMIT_AUTHORITY',
    'PROFESSIONAL_INDEMNITY',
  ],
} as const;

export const DocumentSourceInputSchema = z.discriminatedUnion('source', [
  z.object({
    source: z.literal('OWNER'),
    contactId: z.uuid(),
    input: z.enum(DOCUMENT_SOURCE_INPUTS.OWNER),
  }),
  z.object({
    source: z.literal('PROPERTY'),
    propertyId: z.uuid(),
    input: z.enum(DOCUMENT_SOURCE_INPUTS.PROPERTY),
  }),
  z.object({
    source: z.literal('DEAL'),
    dealId: z.uuid(),
    input: z.enum(DOCUMENT_SOURCE_INPUTS.DEAL),
  }),
  z.object({
    source: z.literal('LOCATION'),
    organizationId: z.uuid(),
    input: z.enum(DOCUMENT_SOURCE_INPUTS.LOCATION),
  }),
  z.object({
    source: z.literal('BROKER'),
    memberId: z.uuid(),
    input: z.enum(DOCUMENT_SOURCE_INPUTS.BROKER),
  }),
  z.object({ source: z.literal('COMPANY'), input: z.enum(DOCUMENT_SOURCE_INPUTS.COMPANY) }),
]);
export type DocumentSourceInput = z.infer<typeof DocumentSourceInputSchema>;

// The inputs a member may set for one document only (IMM-1473): never saved at a source.
export const DOCUMENT_ONLY_INPUTS = {
  LOCATION: ['NAME', 'ADDRESS'],
  BROKER: ['NAME', 'EMAIL', 'PHONE'],
} as const;

export const isDocumentOnlyInput = (input: DocumentSourceInput): boolean =>
  (DOCUMENT_ONLY_INPUTS as Readonly<Record<string, ReadonlyArray<string>>>)[input.source]?.includes(input.input) ??
  false;

const textOf = (text: z.ZodType<string>) => z.object({ kind: z.literal('TEXT'), text });

const LocationAddressValueSchema = z.object({
  kind: z.literal('ADDRESS'),
  address: CreateOrganizationBodySchema.shape.address.pick({
    street: true,
    houseNumber: true,
    postalCode: true,
    city: true,
  }),
});

const documentOnlyPairSchemaOf = (input: DocumentSourceInput): z.ZodType | null => {
  if (input.source === 'LOCATION' && input.input === 'NAME') return textOf(CreateOrganizationBodySchema.shape.name);

  if (input.source === 'LOCATION' && input.input === 'ADDRESS') return LocationAddressValueSchema;

  if (input.source !== 'BROKER') return null;

  if (input.input === 'NAME') return textOf(z.string().trim().min(1).max(200));

  if (input.input === 'EMAIL') return textOf(z.string().trim().pipe(z.email()));

  return textOf(z.string().trim().min(1).max(30));
};

const documentOnlyValueRule = (
  entry: { input: DocumentSourceInput; value: DocumentInputValue },
  ctx: z.core.$RefinementCtx,
) => {
  const pairSchema = isDocumentOnlyInput(entry.input) ? documentOnlyPairSchemaOf(entry.input) : null;

  if (pairSchema === null) {
    ctx.addIssue({ code: 'custom', path: ['input'], message: 'Not a document-only input' });

    return;
  }

  const parsed = pairSchema.safeParse(entry.value);

  if (parsed.success) return;

  for (const issue of parsed.error.issues) {
    ctx.addIssue({ ...issue, path: ['value', ...issue.path] } as unknown as Parameters<typeof ctx.addIssue>[0]);
  }
};

export const DocumentOnlyValueSchema = z
  .object({ input: DocumentSourceInputSchema, value: DocumentInputValueSchema })
  .superRefine(documentOnlyValueRule);
export type DocumentOnlyValue = z.infer<typeof DocumentOnlyValueSchema>;

const noInputTwice = (entries: ReadonlyArray<DocumentOnlyValue>, ctx: z.core.$RefinementCtx) => {
  const seen = new Set<string>();
  entries.forEach((entry, index) => {
    const key = JSON.stringify(entry.input);

    if (seen.has(key)) ctx.addIssue({ code: 'custom', path: [index], message: 'Input given twice' });
    seen.add(key);
  });
};

export const RESOLVED_FIELD_STATES = ['FILLED', 'EMPTY', 'ABSENT_POSITION', 'NOT_APPLICABLE'] as const;

export const ResolvedDocumentFieldSchema = z.object({
  field: DataFieldRefSchema,
  fill: z.enum(['REQUIRED', 'OPTIONAL']), // REQUIRED when any placement is „Pflichtfeld"
  state: z.enum(RESOLVED_FIELD_STATES),
  value: DocumentValueSchema.nullable(), // null unless FILLED; ADDRESS_BLOCKS may be FILLED with a null address inside
  inputs: z.array(DocumentSourceInputSchema), // the source inputs this field reads; [] for {{Datum: Heute}}
});
export type ResolvedDocumentField = z.infer<typeof ResolvedDocumentFieldSchema>;

export const MissingDocumentInputSchema = z.object({
  input: DocumentSourceInputSchema,
  required: z.boolean(),
  // NOT_APPLICABLE inputs (a person field on a company Eigentümer) have no input at any source.
  enterable: z.boolean(),
  fields: z.array(DataFieldRefSchema).min(1), // the data fields waiting on it, for the label
});
export type MissingDocumentInput = z.infer<typeof MissingDocumentInputSchema>;

export const DocumentOwnerSchema = z.object({
  position: z.number().int().min(1),
  contactId: refersTo('contact', z.uuid()),
  organizationId: refersTo('organization', z.uuid()), // the Kontakt's own Standort, for the write (BL-130)
  contactType: z.enum(['PERSON', 'COMPANY']),
  displayName: z.string(),
});
export type DocumentOwner = z.infer<typeof DocumentOwnerSchema>;

export const DealDocumentDraftResponseSchema = z.object({
  template: z.object({
    id: z.uuid(), // own resource echo
    name: z.string(),
    documentType: GeneratedDocumentTypeSchema,
    content: DocumentTemplateContentSchema,
    footer: z.string().nullable(),
    updatedAt: z.iso.datetime(), // the Vorlage's version; generation refuses a stale one
  }),
  owners: z.array(DocumentOwnerSchema).min(1),
  propertyId: refersTo('property', z.uuid()),
  propertyOrganizationId: refersTo('organization', z.uuid()),
  location: z.object({
    organizationId: refersTo('organization', z.uuid()),
    name: z.string(),
    brokers: z.array(z.object({ memberId: refersTo('member', z.uuid()), displayName: z.string() })), // Makler order; [] without Standortleitung
    option: DocumentLocationOptionSchema,
    defaultOption: DocumentLocationOptionSchema, // HEADQUARTERS when the Deal sits on the Hauptstandort, else LOCATION
  }),
  fields: z.array(ResolvedDocumentFieldSchema),
  missing: z.array(MissingDocumentInputSchema), // ordered: OWNER (owner order), PROPERTY, DEAL, LOCATION, BROKER (Makler order), COMPANY
  // The current value of every source input some field reads, null when empty. OWNER.NAME of a person is
  // `PERSON_NAME`, of a company `TEXT`.
  inputValues: z.array(z.object({ input: DocumentSourceInputSchema, value: DocumentInputValueSchema.nullable() })),
  ownerCoverage: z.object({ named: z.number().int().min(1), total: z.number().int().min(2) }).nullable(),
  generatedOn: z.iso.date(), // workspace-local today (Europe/Berlin), the {{Datum: Heute}} value
});
export type DealDocumentDraftResponse = z.infer<typeof DealDocumentDraftResponseSchema>;

export const GENERATED_DOCUMENT_STATUSES = ['GENERATING', 'READY', 'FAILED'] as const;
export const GeneratedDocumentStatusSchema = z.enum(GENERATED_DOCUMENT_STATUSES);
export type GeneratedDocumentStatus = z.infer<typeof GeneratedDocumentStatusSchema>;

export const GENERATED_DOCUMENT_ORIGINS = ['DIALOG', 'RETRY', 'CLIENT'] as const;
export const GeneratedDocumentOriginSchema = z.enum(GENERATED_DOCUMENT_ORIGINS);
export type GeneratedDocumentOrigin = z.infer<typeof GeneratedDocumentOriginSchema>;

export const DealDocumentPathParamsSchema = GetDealPathParamsSchema.extend({ documentId: z.uuid() });
export const SigningRequestPathParamsSchema = GetDealPathParamsSchema.extend({ requestId: z.uuid() });
export const SigningSignerPathParamsSchema = SigningRequestPathParamsSchema.extend({ signerId: z.uuid() });

export const PreviewedDocumentValueSchema = z.object({
  field: DataFieldRefSchema,
  value: DocumentValueSchema.nullable(),
});
export type PreviewedDocumentValue = z.infer<typeof PreviewedDocumentValueSchema>;

export const GenerateDealDocumentBodySchema = z.object({
  templateId: z.uuid(),
  // What the preview showed. All optional: a connected client sends only templateId.
  content: DocumentTemplateContentSchema.nullable().default(null), // non-null only when the text was edited
  checkedBoxes: z.array(z.number().int().min(0)).max(500).default([]), // box indices, document order; used when content is null
  templateUpdatedAt: z.iso.datetime().nullable().default(null), // the draft's template.updatedAt
  previewedValues: z.array(PreviewedDocumentValueSchema).nullable().default(null),
  // Standort and Makler values for this document only; never saved at a source.
  documentOnlyValues: z.array(DocumentOnlyValueSchema).max(50).superRefine(noInputTwice).default([]),
  locationOption: DocumentLocationOptionSchema.nullable().default(null), // null → the default; a connected client never sends it
});
export type GenerateDealDocumentBody = z.infer<typeof GenerateDealDocumentBodySchema>;

export const GeneratedDocumentItemSchema = z.object({
  id: z.uuid(), // own resource
  dealId: refersTo('deal', z.uuid()),
  documentType: GeneratedDocumentTypeSchema,
  templateName: z.string(),
  status: GeneratedDocumentStatusSchema,
  fileName: z.string(),
  manuallyAdjusted: z.boolean(),
  createdByMemberId: refersTo('member', z.uuid()),
  createdAt: z.iso.datetime(),
  completedAt: z.iso.datetime().nullable(),
  canDelete: z.boolean(), // the creator, or a holder of organization:deal:document-manage-any — answered per row
  signing: SigningSummarySchema.nullable(), // the live request, else the newest withdrawn one, else null
  canSendForSignature: z.boolean(), // released ∧ Deal ACTIVE ∧ SALE ∧ document READY ∧ no live request
  deleteLock: z.enum(['SIGNING_OPEN', 'SIGNED']).nullable(),
  originalReceivedAt: z.iso.datetime().nullable(), // POWER_OF_ATTORNEY only: when the paper original was marked received; null otherwise
});
export type GeneratedDocumentItem = z.infer<typeof GeneratedDocumentItemSchema>;

export const GenerateDealDocumentResponseSchema = GeneratedDocumentItemSchema;
export const ListDealDocumentsResponseSchema = z.object({ items: z.array(GeneratedDocumentItemSchema) });
export type ListDealDocumentsResponse = z.infer<typeof ListDealDocumentsResponseSchema>;
export const GetDealDocumentResponseSchema = GeneratedDocumentItemSchema;
export type GetDealDocumentResponse = z.infer<typeof GetDealDocumentResponseSchema>;

export const DEAL_DOCUMENT_FILE_DISPOSITIONS = ['attachment', 'inline'] as const;
export const DealDocumentFileQuerySchema = z.object({
  disposition: z.enum(DEAL_DOCUMENT_FILE_DISPOSITIONS).default('attachment'), // the card downloads; „PDF öffnen" asks for inline
});
export type DealDocumentFileQuery = z.infer<typeof DealDocumentFileQuerySchema>;
export type DealDocumentFileDisposition = DealDocumentFileQuery['disposition'];

export const GetDealDocumentFileResponseSchema = MediaFileReadSchema;

export const SetOriginalReceivedBodySchema = z.object({ received: z.boolean() }).strict();
export type SetOriginalReceivedBody = z.infer<typeof SetOriginalReceivedBodySchema>;
export const SetOriginalReceivedResponseSchema = GeneratedDocumentItemSchema;
export type SetOriginalReceivedResponse = z.infer<typeof SetOriginalReceivedResponseSchema>;

export const DeleteDealDocumentResponseSchema = z.object({ id: z.uuid() }); // own resource
export type DeleteDealDocumentResponse = z.infer<typeof DeleteDealDocumentResponseSchema>;

export const RetryDealDocumentResponseSchema = GeneratedDocumentItemSchema; // the NEW entry
